import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, NewspaperIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import BlogCard from '../components/BlogCard';
import BlogModal from '../components/BlogModal';
import ConfirmDialog from '../components/ConfirmDialog';

const AdminBlogManagementPage = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, postId: null });
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('All');

    const categories = [
        'All',
        'Home Construction Loan',
        'Home Purchase or Resale Loan',
        'Home Affordable Loan',
        'PMAY Urban 2.0',
        'Composite Loan',
        'Home Renovation Loan',
        'Home Extension Loan',
        'Balance Transfer Loan',
        'NRI Home Loan',
        'Top-up Loan',
        'Mortgage Loan'
    ];

    const fetchPosts = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/blog');
            if (response.data.success) {
                setPosts(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching blog posts:', error);
            toast.error('Failed to load blog posts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPosts();
    }, []);

    const handleSubmit = async (formData) => {
        try {
            setSubmitting(true);
            const config = {
                headers: { 'Content-Type': 'multipart/form-data' }
            };

            if (editingPost) {
                await axios.put(`/api/blog/${editingPost.id}`, formData, config);
                toast.success('Blog post updated successfully');
            } else {
                await axios.post('/api/blog', formData, config);
                toast.success('Blog post created successfully');
            }
            setIsModalOpen(false);
            setEditingPost(null);
            fetchPosts();
        } catch (error) {
            console.error('Error saving blog post:', error);
            toast.error(error.response?.data?.message || 'Failed to save blog post');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, postId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/blog/${deleteConfirm.postId}`);
            toast.success('Blog post deleted successfully');
            setDeleteConfirm({ isOpen: false, postId: null });
            fetchPosts();
        } catch (error) {
            console.error('Error deleting blog post:', error);
            toast.error('Failed to delete blog post');
        }
    };

    const handleStatusToggle = async (id, newStatus) => {
        try {
            await axios.patch(`/api/blog/${id}/status`, { is_active: newStatus });
            toast.success(`Post ${newStatus ? 'activated' : 'deactivated'}`);
            setPosts(posts.map(p => p.id === id ? { ...p, is_active: newStatus } : p));
        } catch (error) {
            console.error('Error toggling status:', error);
            toast.error('Failed to update status');
        }
    };

    const filteredPosts = posts.filter(post => {
        const title = post.title || '';
        const excerpt = post.excerpt || '';
        const category = post.category || '';

        const matchesSearch = title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            excerpt.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = filterCategory === 'All' || category === filterCategory;
        return matchesSearch && matchesCategory;
    });

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Blog Management</h1>
                    <p className="text-slate-500 mt-1">Create and manage your articles, guides, and insights</p>
                </div>
                <button
                    onClick={() => {
                        setEditingPost(null);
                        setIsModalOpen(true);
                    }}
                    className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3.5 rounded-2xl font-bold transition-all shadow-lg shadow-primary-100 hover:shadow-xl hover:-translate-y-0.5"
                >
                    <PlusIcon className="h-5 w-5" />
                    Write New Article
                </button>
            </div>

            {/* Filters & Search */}
            <div className="bg-white rounded-3xl p-4 shadow-sm border border-slate-100 flex flex-col md:flex-row gap-4 items-center">
                <div className="relative flex-1 w-full">
                    <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search articles by title or content..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all outline-none"
                    />
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                    <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl text-slate-700 text-sm font-bold outline-none focus:ring-2 focus:ring-primary-500 transition-all cursor-pointer"
                    >
                        {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                    <div className="px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl text-slate-500 text-sm font-bold whitespace-nowrap hidden md:block">
                        Total: {posts.length} Posts
                    </div>
                </div>
            </div>

            {/* Grid Content */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div key={i} className="bg-white rounded-3xl h-[400px] shadow-sm animate-pulse border border-slate-100"></div>
                    ))}
                </div>
            ) : filteredPosts.length === 0 ? (
                <div className="bg-white rounded-[2.5rem] p-16 text-center shadow-sm border border-slate-100">
                    <div className="max-w-md mx-auto">
                        <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
                            <NewspaperIcon className="h-10 w-10 text-slate-300" />
                        </div>
                        <h3 className="text-2xl font-bold text-slate-800 mb-3">No articles found</h3>
                        <p className="text-slate-500 mb-8 leading-relaxed">
                            {searchQuery || filterCategory !== 'All'
                                ? "No blog posts match your current search or filter criteria."
                                : "You haven't written any blog articles yet."}
                        </p>
                        {(searchQuery || filterCategory !== 'All') ? (
                            <button
                                onClick={() => {
                                    setSearchQuery('');
                                    setFilterCategory('All');
                                }}
                                className="text-primary-600 font-bold hover:underline"
                            >
                                Clear All Filters
                            </button>
                        ) : (
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-lg shadow-primary-100"
                            >
                                Start Writing First Post
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredPosts.map((post) => (
                        <BlogCard
                            key={post.id}
                            post={post}
                            onEdit={(p) => {
                                setEditingPost(p);
                                setIsModalOpen(true);
                            }}
                            onDelete={handleDelete}
                            onStatusToggle={handleStatusToggle}
                        />
                    ))}
                </div>
            )}

            {/* Modal */}
            <BlogModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingPost(null);
                }}
                onSubmit={handleSubmit}
                post={editingPost}
                loading={submitting}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, postId: null })}
                onConfirm={confirmDelete}
                title="Delete Article"
                message="Are you sure you want to delete this blog post? This action cannot be undone."
                type="danger"
            />
        </div>
    );
};

export default AdminBlogManagementPage;
